// @ts-nocheck
import React, { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Award, FileText, CheckCircle } from "lucide-react";

const colors = {
  primary: "#19E6FF",
  cardBg: "rgba(15, 23, 42, 0.45)",
  cardBorder: "rgba(255, 255, 255, 0.12)",
  textMuted: "#94a3b8",
};

// Helper: build secure API URLs (routed through /api/contact GET)
const badgeUrl = (file) => `/api/contact?type=badge&file=${encodeURIComponent(file)}`;
const thumbUrl = (file) => `/api/contact?type=thumb&file=${encodeURIComponent(file.replace(".pdf", ".jpg"))}`;

/* ─── Badge data ─── */
const BADGES = [
  { id: "ai-developer-pro",  img: "ibm-ai-developer-professional-certificate.png",          titleEl: "IBM AI Developer Professional Certificate",   certFile: "Coursera NSH483U5R58R.pdf" },
  { id: "gen-ai-essentials", img: "generative-ai-essentials.png",                           titleEl: "Generative AI Essentials",                    certFile: "Coursera QCZWYU38V2SX.pdf" },
  { id: "gen-ai-dev",        img: "generative-ai-essentials-for-software-developers.1.png", titleEl: "Generative AI for Software Developers",       certFile: "Coursera 4773PSY25WZX.pdf" },
  { id: "multimodal-ai",     img: "build-multimodal-generative-ai-applications.png",        titleEl: "Build Multimodal Generative AI Apps",         certFile: "Coursera AHHGN5OUYDBN.pdf" },
  { id: "python-ai",         img: "python-for-data-science-and-ai.png",                     titleEl: "Python for Data Science & AI",                certFile: "Coursera AJE9YXVQJMUV.pdf" },
  { id: "python-project",    img: "python-project-for-ai-and-application-development.png",  titleEl: "Python Project for AI & App Development",    certFile: "Coursera EK7FA0QNH499.pdf" },
  { id: "software-eng",      img: "software-engineering-essentials.png",                    titleEl: "Software Engineering Essentials",             certFile: "Coursera F4LLDC3CCAK2.pdf" },
];

/* ─── Certificate data ─── */
const CERTIFICATES = [
  { id: "c1",  file: "Coursera NSH483U5R58R.pdf", titleEl: "IBM AI Developer Professional Certificate",  year: "2024" },
  { id: "c2",  file: "Coursera QCZWYU38V2SX.pdf", titleEl: "Generative AI Essentials",                  year: "2024" },
  { id: "c3",  file: "Coursera 4773PSY25WZX.pdf", titleEl: "Generative AI for Software Developers",     year: "2024" },
  { id: "c4",  file: "Coursera AHHGN5OUYDBN.pdf", titleEl: "Build Multimodal Generative AI Apps",       year: "2024" },
  { id: "c5",  file: "Coursera AJE9YXVQJMUV.pdf", titleEl: "Python for Data Science & AI",             year: "2024" },
  { id: "c6",  file: "Coursera EK7FA0QNH499.pdf", titleEl: "Python Project for AI & App Development",  year: "2024" },
  { id: "c7",  file: "Coursera F4LLDC3CCAK2.pdf", titleEl: "Software Engineering Essentials",          year: "2024" },
  { id: "c8",  file: "Coursera GARKFU6UY8ZB.pdf", titleEl: "Generative AI: Prompt Engineering Basics", year: "2024" },
  { id: "c9",  file: "Coursera GJT3KYDSR7QR.pdf", titleEl: "AI & Machine Learning Foundations",        year: "2024" },
  { id: "c10", file: "Coursera K4ZAESAGM9UK.pdf", titleEl: "Gen AI: Foundation Models & Platforms",    year: "2024" },
  { id: "c11", file: "Coursera R2FPN2EFHPYW.pdf", titleEl: "Gen AI: Elevate your Software Dev Career", year: "2024" },
  { id: "c12", file: "Coursera SMXFKD4FG7GX.pdf", titleEl: "Developing AI Apps with Python & Flask",   year: "2024" },
  { id: "c13", file: "Coursera UKCSRKMX36BZ.pdf", titleEl: "Introduction to Software Engineering",     year: "2024" },
  { id: "c14", file: "Coursera UPS76EOXEWYU.pdf", titleEl: "Introduction to Cloud Computing",          year: "2024" },
  { id: "c15", file: "Coursera 5A667LZJP66Z.pdf", titleEl: "Introduction to HTML, CSS & JavaScript",  year: "2024" },
  { id: "c16", file: "Coursera 9O98OXCQDQ27.pdf", titleEl: "Gen AI: LLMs & Text Generation",          year: "2024" },
];

/* ─── Modal: view-only PDF/image, no download button ─── */
function ViewModal({ item, onClose }) {
  return (
    <AnimatePresence>
      {item && (
        <motion.div
          key="overlay"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[999] flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.88)", backdropFilter: "blur(10px)" }}
          onClick={onClose}
        >
          <motion.div
            key="box"
            initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.92, opacity: 0 }}
            transition={{ type: "spring", stiffness: 280, damping: 24 }}
            className="relative w-full max-w-3xl rounded-2xl overflow-hidden border"
            style={{ background: "rgba(8,12,28,0.98)", borderColor: colors.cardBorder, maxHeight: "92vh" }}
            onClick={(e) => e.stopPropagation()}
            // Prevent right-click on modal
            onContextMenu={(e) => e.preventDefault()}
          >
            {/* Header — NO download/open link */}
            <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: colors.cardBorder }}>
              <div className="flex items-center gap-2 min-w-0">
                <CheckCircle size={14} className="text-cyan-400 shrink-0" />
                <span className="text-sm font-semibold text-cyan-400 truncate">{item.title}</span>
              </div>
              <button
                onClick={onClose}
                className="ml-3 shrink-0 p-1.5 rounded-lg hover:bg-white/10 transition-colors text-slate-400 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            {/* Browser-independent image preview; avoids blocked sandboxed PDF viewers. */}
            <div className="flex items-center justify-center p-4 sm:p-8 bg-black/20" style={{ minHeight: "60vh" }}>
              <img
                src={item.url}
                alt={item.title}
                className="max-w-full max-h-[72vh] object-contain rounded-xl drop-shadow-2xl"
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
              />
            </div>

            {/* Footer watermark */}
            <div className="px-5 py-2 border-t text-center text-[11px] text-slate-600" style={{ borderColor: colors.cardBorder }}>
              © Δημήτριος Δέλλιος — Orion Intelligence · {new Date().getFullYear()}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ─── Certificate card with real thumbnail ─── */
function CertCard({ cert, index, onClick, isEl }) {
  return (
    <motion.button
      variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.4 } } }}
      whileHover={{ y: -5, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="group relative w-full text-left rounded-2xl overflow-hidden border transition-all duration-300"
      style={{ background: colors.cardBg, borderColor: colors.cardBorder, backdropFilter: "blur(12px)" }}
    >
      {/* Thumbnail image — real certificate preview */}
      <div className="relative overflow-hidden bg-white" style={{ aspectRatio: "640/494" }}>
        <img
          src={thumbUrl(cert.file)}
          alt={cert.titleEl}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
          draggable={false}
          onContextMenu={(e) => e.preventDefault()}
        />
        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
          <span className="text-white text-sm font-bold opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 px-4 py-2 rounded-xl backdrop-blur-sm">
            {isEl ? "Προβολή πιστοποιητικού →" : "View certificate →"}
          </span>
        </div>
        {/* Number badge overlay */}
        <div
          className="absolute top-2 right-2 text-[10px] font-black px-2 py-0.5 rounded-lg"
          style={{ background: "rgba(0,0,0,0.65)", color: "#19E6FF", backdropFilter: "blur(4px)" }}
        >
          #{String(index + 1).padStart(2, "0")}
        </div>
      </div>

      {/* Card footer */}
      <div className="px-3 py-2.5 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold text-slate-300 group-hover:text-white transition-colors truncate">
            {cert.titleEl}
          </p>
          <div className="flex items-center gap-1 mt-0.5">
            <CheckCircle size={10} className="text-emerald-400 shrink-0" />
            <span className="text-[10px] text-emerald-400">{isEl ? "Επαληθευμένο" : "Verified"} · {cert.year}</span>
          </div>
        </div>
        <div
          className="shrink-0 text-[9px] font-black tracking-wider px-2 py-1 rounded-lg"
          style={{ background: "rgba(25,230,255,0.1)", color: "#19E6FF", border: "1px solid rgba(25,230,255,0.2)" }}
        >
          IBM
        </div>
      </div>
    </motion.button>
  );
}

/* ─── Main export ─── */
export default function CertificationsSection({ lang = "el" }) {
  const [modal, setModal] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const isEl = lang === "el";
  const visibleBadges = showAll ? BADGES : BADGES.slice(0, 4);
  const visibleCertificates = showAll ? CERTIFICATES : CERTIFICATES.slice(0, 3);

  const openCert  = useCallback((file, title) => setModal({ url: thumbUrl(file), title }), []);
  const openBadge = useCallback((file, title) => setModal({ url: badgeUrl(file), title }), []);
  const closeModal = useCallback(() => setModal(null), []);

  return (
    <>
      <ViewModal item={modal} onClose={closeModal} />

      <motion.section
        id="certifications"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-10%" }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
      >
        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-cyan-500/30 bg-cyan-500/10 text-cyan-400 text-xs font-semibold mb-3">
              <Award size={12} />
              <span>{isEl ? "Πιστοποιημένη Τεχνογνωσία" : "Certified Expertise"}</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold">
              {isEl ? "Πιστοποιήσεις & Επιτεύγματα" : "Certifications & Achievements"}
            </h2>
            <p className="mt-2 text-sm max-w-xl" style={{ color: colors.textMuted }}>
              {isEl
                ? "Συνεχής εκπαίδευση και επαλήθευση γνώσεων μέσω επίσημων προγραμμάτων IBM στην Τεχνητή Νοημοσύνη, Cloud Computing και Software Engineering."
                : "Continuous learning and verified expertise through official IBM programs in AI, Cloud Computing and Software Engineering."}
            </p>
          </div>
          {/* Stats */}
          <div className="shrink-0 flex items-center gap-4 px-5 py-3 rounded-2xl border" style={{ background: colors.cardBg, borderColor: colors.cardBorder }}>
            <div className="text-center">
              <div className="text-2xl font-black" style={{ color: colors.primary }}>7</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Badges</div>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div className="text-center">
              <div className="text-2xl font-black" style={{ color: "#C084FC" }}>16</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">{isEl ? "Πιστοποιητικά" : "Certificates"}</div>
            </div>
          </div>
        </div>

        {/* ── IBM Badges ── */}
        <h3 className="text-sm font-bold uppercase tracking-widest text-slate-400 mb-5 flex items-center gap-2">
          <Award size={14} className="text-cyan-400" /> IBM Digital Badges
        </h3>
        <motion.div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-16"
          variants={{ hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.07 } } }}
          initial="hidden" whileInView="show" viewport={{ once: true }}
        >
          {visibleBadges.map((badge) => (
            <motion.button
              key={badge.id}
              variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.4 } } }}
              whileHover={{ y: -6, scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => openBadge(badge.img, isEl ? badge.titleEl : badge.titleEl)}
              className="group relative flex flex-col items-center gap-3 p-5 rounded-2xl border transition-all duration-300 cursor-pointer"
              style={{ background: colors.cardBg, borderColor: colors.cardBorder, backdropFilter: "blur(12px)" }}
            >
              <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{ background: "radial-gradient(circle at 50% 0%, rgba(25,230,255,0.12), transparent 70%)" }} />
              <img
                src={badgeUrl(badge.img)}
                alt={badge.titleEl}
                className="w-16 h-16 object-contain drop-shadow-lg"
                loading="lazy"
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
              />
              <span className="text-[11px] font-semibold text-center leading-snug text-slate-300 group-hover:text-white transition-colors">
                {badge.titleEl}
              </span>
              <span className="text-[10px] text-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity font-medium">
                {isEl ? "Προβολή →" : "View →"}
              </span>
            </motion.button>
          ))}
        </motion.div>

        {/* ── Certificates ── */}
        <h3 className="text-sm font-bold uppercase tracking-widest text-slate-400 mb-5 flex items-center gap-2">
          <FileText size={14} className="text-purple-400" />
          {isEl ? "Coursera / IBM Certificates" : "Coursera / IBM Certificates"}
        </h3>
        <motion.div
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
          variants={{ hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.05 } } }}
          initial="hidden" whileInView="show" viewport={{ once: true }}
        >
          {visibleCertificates.map((cert, i) => (
            <CertCard
              key={cert.id}
              cert={cert}
              index={i}
              isEl={isEl}
              onClick={() => openCert(cert.file, isEl ? cert.titleEl : cert.titleEl)}
            />
          ))}
        </motion.div>

        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={() => setShowAll((value) => !value)}
            className="rounded-2xl border border-cyan-400/30 bg-cyan-400/10 px-6 py-3 text-sm font-bold text-cyan-300 transition-colors hover:bg-cyan-400/15"
            aria-expanded={showAll}
          >
            {showAll
              ? (isEl ? "Εμφάνιση λιγότερων" : "Show fewer")
              : (isEl ? "Προβολή όλων των πιστοποιήσεων" : "View all certifications")}
          </button>
        </div>

        {/* Bottom note */}
        <div className="mt-8 px-5 py-4 rounded-2xl border flex items-start gap-3"
          style={{ background: "rgba(25,230,255,0.03)", borderColor: "rgba(25,230,255,0.1)" }}>
          <Award size={16} className="text-cyan-400 shrink-0 mt-0.5" />
          <p className="text-[12px] leading-relaxed" style={{ color: colors.textMuted }}>
            {isEl
              ? "Όλα τα πιστοποιητικά έχουν εκδοθεί από IBM & Coursera. Κάντε κλικ σε οποιοδήποτε badge ή πιστοποιητικό για προβολή."
              : "All certificates are issued by IBM & Coursera. Click any badge or certificate to preview."}
          </p>
        </div>
      </motion.section>
    </>
  );
}
